import React, { useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import SearchableSelect from "../../components/common/SearchableSelect";
import { errorAlert, successAlert } from "../../utils/alert";

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const VolunteerRegistrations = () => {
  const { role } = useContext(AuthContext);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const isAllowed = ["admin", "secretary", "district"].includes((role || "").toLowerCase());

  const loadData = async () => {
    setLoading(true);
    try {
      const [regsRes, statsRes, eventsRes] = await Promise.all([
        API.fetchVolunteerRegistrations(),
        API.fetchVolunteerRegistrationStats(),
        API.fetchAdminEvents(),
      ]);

      setRegistrations(regsRes?.data || regsRes || []);
      setStats(statsRes?.data || statsRes || []);
      setEvents(eventsRes?.data || eventsRes || []);
    } catch (err) {
      errorAlert("Load failed", err?.message || "Failed to load volunteer registrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;
    loadData();
  }, [isAllowed]);

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(registrations) ? registrations : [];
    return rows.filter((row) => {
      if (selectedEvent && String(row.event_id) !== String(selectedEvent)) return false;
      if (statusFilter !== "All" && String(row.status) !== statusFilter) return false;
      return true;
    });
  }, [registrations, selectedEvent, statusFilter]);

  const handleStatusChange = async (row, nextStatus) => {
    if (!nextStatus || nextStatus === row.status) return;

    let note = "";
    if (nextStatus === "Rejected") {
      const reasonRes = await Swal.fire({
        title: "Reject Volunteer?",
        text: "You can add optional note.",
        input: "text",
        inputPlaceholder: "Reason (optional)",
        showCancelButton: true,
        confirmButtonText: "Reject",
      });
      if (!reasonRes.isConfirmed) return;
      note = String(reasonRes.value || "").trim();
    } else {
      const confirm = await Swal.fire({
        title: `${nextStatus} Volunteer?`,
        text: `Status will change to ${nextStatus}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      await API.updateVolunteerRegistrationStatus(row.id, nextStatus, note);
      successAlert("Status updated", `Volunteer registration ${nextStatus.toLowerCase()} successfully.`);
      await loadData();
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update status.");
    }
  };

  const downloadCertificate = async (row, regenerate = false) => {
    try {
      await API.downloadVolunteerCertificate(row.id, { regenerate });
      successAlert(
        "Certificate ready",
        regenerate ? "Certificate regenerated and downloaded." : "Certificate downloaded."
      );
      await loadData();
    } catch (err) {
      errorAlert("Download failed", err?.message || "Failed to download certificate.");
    }
  };

  if (!isAllowed) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Volunteer Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Volunteer / Escort Registrations</h1>
              <p className="text-sm text-slate-600 mt-1">
                Review volunteer entries, approve status and issue certificates from a single queue.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Rows</p>
                <p className="text-lg font-semibold text-slate-900">{filteredRows.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Events</p>
                <p className="text-lg font-semibold text-slate-900">{events.length}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Status</p>
                <p className="text-lg font-semibold text-amber-800">{statusFilter}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Loading</p>
                <p className="text-lg font-semibold text-emerald-800">{loading ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {(Array.isArray(stats) ? stats : []).map((s) => (
            <div key={s.event_id} className="p-4 border rounded-xl bg-white shadow-sm">
              <h3 className="font-semibold text-blue-700 mb-2">{s.event_title}</h3>
              <p>Pending: {s.pending || 0}</p>
              <p className="text-green-700">Approved: {s.approved || 0}</p>
              <p className="text-red-600">Rejected: {s.rejected || 0}</p>
            </div>
          ))}
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Event</label>
            <SearchableSelect
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="border p-2 rounded w-64"
            >
              <option value="">All Events</option>
              {(Array.isArray(events) ? events : []).map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <SearchableSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border p-2 rounded w-48"
            >
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </SearchableSelect>
          </div>
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 shadow overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-2.5">
            <h2 className="text-lg font-semibold text-slate-900">Volunteer Queue</h2>
            <p className="text-sm text-slate-600">Update status and handle certificate downloads for approved records.</p>
          </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-500">Loading volunteer registrations...</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No volunteer registrations found.</div>
          ) : (
            <table className="min-w-[1200px] w-full text-xs sm:text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2">Submitted</th>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Father Name</th>
                  <th className="text-left px-3 py-2">Age</th>
                  <th className="text-left px-3 py-2">Organisation</th>
                  <th className="text-left px-3 py-2">Mobile</th>
                  <th className="text-left px-3 py-2">District</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className="border-t align-middle">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.event_title || "-"}</div>
                      <div className="text-xs text-slate-500">
                        {formatDate(row.event_date)} {row.event_location ? `| ${row.event_location}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">{row.name || "-"}</td>
                    <td className="px-3 py-2">{row.father_name || "-"}</td>
                    <td className="px-3 py-2">{row.age || "-"}</td>
                    <td className="px-3 py-2">{row.organisation || "-"}</td>
                    <td className="px-3 py-2">{row.mobile || "-"}</td>
                    <td className="px-3 py-2">{row.district || "-"}</td>
                    <td className="px-3 py-2">
                      <SearchableSelect
                        className="border px-2 py-1 rounded text-xs min-w-[110px]"
                        value={row.status || "Pending"}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </SearchableSelect>
                    </td>
                    <td className="px-3 py-2">
                      {row.status === "Approved" ? (
                        <div>
                          <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto whitespace-nowrap pb-0.5">
                            <button
                              onClick={() => downloadCertificate(row, false)}
                              className="px-2.5 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => downloadCertificate(row, true)}
                              className="px-2.5 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700"
                            >
                              Regenerate
                            </button>
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {row.certificate_no ? `No: ${row.certificate_no}` : "Not issued yet"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-500">Approve to issue certificate</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </section>
      </div>
    </>
  );
};

export default VolunteerRegistrations;
